import React, { useEffect, useState } from 'react';
import { Archive, Building2, Calendar as CalIcon, Clock, Copy, Edit3, Eye, Merge, MoreHorizontal, Split, Trash2, User } from 'lucide-react';
import StatusBadge from './StatusBadge';
import WorkflowActions from './WorkflowActions';
import TagsManager from './TagsManager';
import CommentsPanel from './CommentsPanel';

/** @param {{entry:any, clients:any[], projects:any[], onEdit:Function, onDuplicate:Function, onDelete:Function, onStatusUpdate:Function, onAddComment:Function, onUpdateTags:Function, onSplit?:Function, onRevertSplit?:Function, userRole:string, teamMembers?:Array}} props */
export default function TimeEntryCard(props) {
  const {
    entry, clients, projects, onEdit, onDuplicate, onDelete,
    onStatusUpdate, onAddComment, onUpdateTags, onSplit, onRevertSplit,
    userRole, teamMembers
  } = props;
   // ADD THIS DEBUG
  if (entry.id === 'f2855614-b39e-48cd-a057-89878af26274') {
    console.log('TimeEntryCard received entry with comments:', entry.comments);
  }

  const [showActions, setShowActions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSplitDetails, setShowSplitDetails] = useState(false);
  const [editValues, setEditValues] = useState({
    task_name: entry.task_name,
    duration_hours: entry.duration_hours,
    description: entry.description || '',
    is_billable: entry.is_billable,
    user_id: entry.user_id
  });

  const client = entry.client_name 
  ? { id: entry.client_id, name: entry.client_name }
  : clients?.find(c => c.id === entry.client_id) || { name: 'Unknown Client' };

const project = entry.project_name
  ? { id: entry.project_id, name: entry.project_name, color: entry.project_color || '#6B7280' }
  : projects?.find(p => p.id === entry.project_id) || { name: 'Unknown Project', color: '#6B7280' };

  const formatTime = (hours) => {
    const h = Math.floor(hours); const m = Math.floor((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const calculateRevenue = () => {
    if (entry.is_split && entry.splits) {
      return entry.splits.reduce((t, s) => t + (s.is_billable ? s.duration_hours * s.hourly_rate : 0), 0);
    }
    return entry.is_billable ? (entry.duration_hours * (entry.hourly_rate || 0)) : 0;
  };

  const canEdit = entry.user_id === 'current-user' || ['admin','manager'].includes(userRole);
  const isAdmin = userRole === 'admin';
  const canSplit = !entry.is_split && ['admin','manager'].includes(userRole);
  const canRevertSplit = entry.is_split && ['admin','manager'].includes(userRole);

  useEffect(() => {
    if (showEditModal) {
      setEditValues({
        task_name: entry.task_name,
        duration_hours: entry.duration_hours,
        description: entry.description || '',
        is_billable: entry.is_billable,
        user_id: entry.user_id
      });
    }
  }, [showEditModal, entry]);

  const save = () => { onEdit(entry.id, editValues); setShowEditModal(false); };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all duration-200" style={{ borderLeft: `4px solid ${project.color}` }}>
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h4 className="font-semibold text-slate-800">{entry.task_name}</h4>
              <StatusBadge status={entry.status} priority={entry.priority} />
              {entry.is_split && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  <Split className="w-3 h-3 mr-1" /> Split Entry
                </span>
              )}
            </div>

            <div className="flex items-center space-x-4 text-sm text-slate-600">
              <div className="flex items-center space-x-1"><Building2 className="w-4 h-4" /><span>{client.name}</span></div>
              <div className="flex items-center space-x-1"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }} /><span>{project.name}</span></div>
              <div className="flex items-center space-x-1">
                <User className="w-4 h-4" />
                <span>{teamMembers?.find(m => m.user_id === entry.user_id)?.full_name || teamMembers?.find(m => m.user_id === entry.user_id)?.email || 'Unknown User'}</span>
                {isAdmin && <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded" title="Admin can reassign">★</span>}
              </div>
              <div className="flex items-center space-x-1"><CalIcon className="w-4 h-4" /><span>{new Date(entry.entry_date).toLocaleDateString()}</span></div>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="flex items-center space-x-1 text-lg font-semibold text-slate-800">
                <Clock className="w-4 h-4" />
                <span>{formatTime(entry.duration_hours)}</span>
              </div>
              {entry.is_split ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Mixed</span>
              ) : (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${entry.is_billable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                  {entry.is_billable ? 'Billable' : 'Non-billable'}
                </span>
              )}
            </div>

            <WorkflowActions entry={entry} onStatusUpdate={onStatusUpdate} userRole={userRole} teamMembers={teamMembers} />

            <div className="relative">
              <button onClick={() => setShowActions(!showActions)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <MoreHorizontal className="w-4 h-4" />
              </button>

              {showActions && (
                <div className="absolute right-0 top-10 bg-white border border-slate-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]">
                  <button onClick={() => { setShowSplitDetails(!showSplitDetails); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center">
                    <Eye className="w-4 h-4 mr-2" /> View Details
                  </button>

                  {canEdit && (
                    <button onClick={() => { setShowEditModal(true); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center">
                      <Edit3 className="w-4 h-4 mr-2" /> {isAdmin ? 'Edit & Reassign' : 'Edit'}
                    </button>
                  )}

                  {canSplit && onSplit && (
                    <button onClick={() => { onSplit(entry); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center">
                      <Split className="w-4 h-4 mr-2" /> Split Time
                    </button>
                  )}

                  {canRevertSplit && onRevertSplit && (
                    <button onClick={() => { onRevertSplit(entry); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center">
                      <Merge className="w-4 h-4 mr-2" /> Revert Split
                    </button>
                  )}

                  <button onClick={() => { onDuplicate(entry); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center">
                    <Copy className="w-4 h-4 mr-2" /> Duplicate
                  </button>

                  <button onClick={() => { /* archive hook if you add later */ setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center">
                    <Archive className="w-4 h-4 mr-2" /> Archive
                  </button>

                  <hr className="my-1" />

                  <button onClick={() => { onDelete(entry.id); setShowActions(false); }} className="w-full text-left px-3 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {entry.description && <p className="text-sm text-slate-600 mb-3">{entry.description}</p>}

        {/* Split details preview */}
        {entry.is_split && entry.splits && (
          <div className="mb-3 border border-blue-200 bg-blue-50 rounded-lg p-3">
            <button onClick={() => setShowSplitDetails(!showSplitDetails)} className="flex items-center justify-between w-full text-left">
              <div className="flex items-center space-x-2 text-sm font-medium text-blue-800">
                <Split className="w-4 h-4" /><span>Split Details ({entry.splits.length} components)</span>
              </div>
              {showSplitDetails ? <span className="text-blue-600">▲</span> : <span className="text-blue-600">▼</span>}
            </button>

            {showSplitDetails && (
              <div className="mt-3 space-y-2">
                {entry.splits.map((s, i) => (
                  <div key={s.id} className="bg-white rounded p-2 border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">{s.description}</p>
                        <p className="text-xs text-slate-600">Component {i + 1}</p>
                      </div>
                      <div className="text-right text-sm">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{formatTime(s.duration_hours)}</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${s.is_billable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                            {s.is_billable ? 'Billable' : 'Non-billable'}
                          </span>
                        </div>
                        <p className="text-xs mt-1 text-slate-600">{s.is_billable ? `$${(s.duration_hours * s.hourly_rate).toFixed(2)}` : '$0.00'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <TagsManager tags={entry.tags} onUpdateTags={onUpdateTags} entryId={entry.id} />

        {showDetails && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="font-medium text-slate-700">Created:</span><span className="ml-2 text-slate-600">{new Date(entry.created_at).toLocaleString()}</span></div>
              {entry.submitted_at && <div><span className="font-medium text-slate-700">Submitted:</span><span className="ml-2 text-slate-600">{new Date(entry.submitted_at).toLocaleString()}</span></div>}
              {entry.approved_at && <div><span className="font-medium text-slate-700">Approved:</span><span className="ml-2 text-slate-600">{new Date(entry.approved_at).toLocaleString()}</span></div>}
              <div><span className="font-medium text-slate-700">Hourly Rate:</span><span className="ml-2 text-slate-600">${entry.hourly_rate}/hr</span></div>
              {entry.is_split && (
                <>
                  <div><span className="font-medium text-slate-700">Split By:</span><span className="ml-2 text-slate-600">{entry.split_by_name || 'Manager'}</span></div>
                  <div><span className="font-medium text-slate-700">Split Date:</span><span className="ml-2 text-slate-600">{entry.split_at ? new Date(entry.split_at).toLocaleString() : 'Unknown'}</span></div>
                </>
              )}
              {isAdmin && <div><span className="font-medium text-slate-700">User ID:</span><span className="ml-2 text-slate-600 font-mono text-xs">{entry.user_id}</span></div>}
            </div>
          </div>
        )}

        <CommentsPanel comments={entry.comments} onAddComment={onAddComment} entryId={entry.id} />
      </div>

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              {isAdmin ? 'Edit Time Entry & Assign User' : 'Edit Time Entry'}
            </h3>

            {entry.is_split && (
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm">
                  <p className="font-medium text-purple-800">Split Entry</p>
                  <p className="text-blue-700">Editing affects only the main entry. Duration/billable are managed by components.</p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              {isAdmin && teamMembers?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assign to User</label>
                  <select
                    value={editValues.user_id}
                    onChange={(e) => setEditValues((p) => ({ ...p, user_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select user...</option>
                    {teamMembers.map(m => (
                      <option key={m.user_id} value={m.user_id}>
                        {m.full_name || m.email} ({m.role}){m.user_id === entry.user_id ? ' (Current)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Task Name</label>
                <input
                  type="text"
                  value={editValues.task_name}
                  onChange={(e) => setEditValues((p) => ({ ...p, task_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Duration (hours)</label>
                <input
                  type="number" step="0.25" value={editValues.duration_hours}
                  onChange={(e) => setEditValues((p) => ({ ...p, duration_hours: parseFloat(e.target.value) }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={entry.is_split}
                />
                {entry.is_split && <p className="text-xs text-amber-600 mt-1">Revert split to change duration.</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={editValues.description}
                  onChange={(e) => setEditValues((p) => ({ ...p, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>

              <div className="flex items-center">
                <input
                  id="editBillable"
                  type="checkbox"
                  checked={editValues.is_billable}
                  onChange={(e) => setEditValues((p) => ({ ...p, is_billable: e.target.checked }))}
                  className="mr-2 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  disabled={entry.is_split}
                />
                <label htmlFor="editBillable" className="text-sm text-slate-700">
                  Billable {entry.is_split && <span className="text-amber-600 ml-1">(managed by split components)</span>}
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setShowEditModal(false)} className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium">Cancel</button>
              <button onClick={save} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                {isAdmin && editValues.user_id !== entry.user_id ? 'Save & Reassign' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
